import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Server, Shield, Cloud, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TrustSection from '../components/trust/TrustSection';
import FaqSection from '../components/trust/FaqSection';
import { products } from '../data/productRegistry';
import { useLocalizedProducts } from '../hooks/useLocalizedProducts';

const tierIds = ['selfHosted', 'perInstance', 'managed'] as const;
const tierIcons = [Server, Shield, Cloud];
const tierAccents = ['from-slate-500 to-slate-700', 'from-cyan-400 to-sky-500', 'from-emerald-400 to-teal-500'];

const PricingPage = () => {
  const { t } = useTranslation('editor');
  const localizedProducts = useLocalizedProducts();

  const platform = products.filter((p) => p.category === 'platform' || p.category === 'collaboration');
  const identity = products.filter((p) => p.category === 'identity');
  const infra = products.filter((p) => p.category === 'infrastructure' || p.category === 'migration');

  const productGroups = [
    { label: t('pricingPage.perProduct.collaboration'), products: platform },
    { label: t('pricingPage.perProduct.identity'), products: identity },
    { label: t('pricingPage.perProduct.infrastructure'), products: infra },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60 mask-fade-b" />
        <div className="pointer-events-none absolute top-20 left-1/2 -translate-x-1/2 w-[900px] h-[420px] rounded-full bg-gradient-to-r from-blue-600/25 via-cyan-500/20 to-emerald-500/15 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] text-cyan-200 mb-6">
              <Tag className="h-3.5 w-3.5" />
              <span className="text-sm font-semibold tracking-wide">{t('pricingPage.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-5">{t('pricingPage.title')}</h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">{t('pricingPage.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className="relative pb-24 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 items-stretch">
            {tierIds.map((id, i) => {
              const TierIcon = tierIcons[i];
              const highlighted = id === 'perInstance';
              const rawFeatures = t(`pricingPage.tiers.${id}.features`, { returnObjects: true });
              const features = Array.isArray(rawFeatures) ? (rawFeatures as string[]) : [];
              const href = id === 'selfHosted' ? 'https://github.com/ScaleNix' : '/contact';
              const ctaClass = `group flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full font-semibold transition-all ${
                highlighted
                  ? 'text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_32px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_48px_-6px_rgba(34,211,238,1)]'
                  : 'text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.1]'
              }`;
              const cta = (
                <>
                  {t(`pricingPage.tiers.${id}.cta`)}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              );
              return (
                <motion.div
                  key={id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`relative rounded-3xl ${highlighted ? 'p-px bg-gradient-to-b from-cyan-300/70 via-sky-400/30 to-emerald-300/40 lg:-translate-y-4' : ''}`}
                >
                  <div className={`relative h-full flex flex-col rounded-3xl p-8 ${highlighted ? 'bg-slate-900' : 'glass-card'}`}>
                    {highlighted && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-slate-950 bg-gradient-to-r from-cyan-300 to-emerald-300">
                        {t('pricingPage.mostPopular')}
                      </span>
                    )}
                    <div className={`inline-flex w-fit p-3 rounded-2xl bg-gradient-to-br ${tierAccents[i]} mb-5`}>
                      <TierIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-1">{t(`pricingPage.tiers.${id}.name`)}</h3>
                    <p className="text-sm text-slate-400 mb-5">{t(`pricingPage.tiers.${id}.tagline`)}</p>
                    <div className="font-display text-3xl font-bold text-white mb-4">{t(`pricingPage.tiers.${id}.price`)}</div>
                    <p className="text-sm text-slate-400 leading-relaxed mb-6">{t(`pricingPage.tiers.${id}.description`)}</p>
                    <ul className="space-y-3 mb-8 flex-1">
                      {features.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-2.5 text-sm text-slate-300">
                          <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-emerald-400/15 flex items-center justify-center">
                            <Check className="h-3 w-3 text-emerald-300" />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    {href.startsWith('http') ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className={ctaClass}>{cta}</a>
                    ) : (
                      <Link to={href} className={ctaClass}>{cta}</Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-3">{t('pricingPage.perProduct.title')}</h2>
            <p className="text-lg text-slate-500">{t('pricingPage.perProduct.subtitle')}</p>
          </motion.div>

          {productGroups.map((group, gi) => (
            <div key={gi} className="mb-12 last:mb-0">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em] mb-5">{group.label}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.products.map((p) => {
                  const locP = localizedProducts.find((lp) => lp.slug === p.slug);
                  return (
                    <Link
                      key={p.slug}
                      to={`/products/${p.slug}`}
                      className="group flex flex-col rounded-2xl p-5 bg-slate-50 border border-slate-200/70 hover:bg-white hover:border-transparent hover:shadow-[0_20px_50px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-0.5 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-xl bg-gradient-to-br ${p.accent}`}>
                          <p.icon className="h-4 w-4 text-white" />
                        </div>
                        <div className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{locP?.name ?? p.name}</div>
                        <ArrowRight className="ml-auto h-4 w-4 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{locP?.pricingNote ?? p.pricingNote}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <TrustSection />
      <FaqSection />
      <Footer />
    </div>
  );
};

export default PricingPage;
